import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Flag, Loader2, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const REPORT_REASONS = [
  { key: "spam", label: "Spam" },
  { key: "harassment", label: "Harassment or bullying" },
  { key: "hate", label: "Hate speech" },
  { key: "nudity", label: "Nudity or sexual content" },
  { key: "violence", label: "Violence or threats" },
  { key: "scam", label: "Scam or fraud" },
  { key: "misinformation", label: "False information" },
  { key: "other", label: "Something else" },
];

export default function ReportDialog({ target, onClose }) {
  const [reason, setReason] = useState(null);
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (target) { setReason(null); setDetails(""); } }, [target]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reports", { target_type: target.type, target_id: target.id, reason, details });
      toast.success("Thanks — our moderators will review this report.");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="report-dialog" className="bg-card border-border rounded-2xl max-w-md z-[80]">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Flag className="w-5 h-5 text-[#E024A3]" />Report {target?.label || target?.type}</DialogTitle>
          <DialogDescription>Your report is anonymous. What's wrong with it?</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {REPORT_REASONS.map((r) => (
            <button
              key={r.key}
              data-testid={`report-reason-${r.key}`}
              onClick={() => setReason(r.key)}
              className={`flex items-center justify-between gap-2 text-left text-sm px-3 py-2.5 rounded-xl border-2 transition-colors ${reason === r.key ? "border-[#8012C6] bg-[#6C0BA9] text-white font-semibold shadow-lg shadow-[#6C0BA9]/30" : "border-border hover:bg-accent"}`}
            >
              {r.label}
              {reason === r.key && <Check className="w-4 h-4 shrink-0" />}
            </button>
          ))}
        </div>
        <textarea
          data-testid="report-details-input"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          maxLength={1000}
          rows={3}
          placeholder="Add details (optional)"
          className="w-full bg-input border border-border rounded-xl px-3 py-2 text-sm resize-none outline-none focus:border-[#6C0BA9]"
        />
        <Button data-testid="report-submit-btn" disabled={!reason || busy} onClick={submit} className="w-full rounded-xl bg-[#6C0BA9] hover:bg-[#8012C6] text-white">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit report"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
